import AppManagerClient from '../../api/AppManagerClient';
import { ApiNonSuccessError, IManagingApplicationInstance, IManagingVirtualAppsInstance, IPostApplicationActionResponse } from '../../api/models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PopupProps } from '../../areas/shared/Popup';
import { AccordionWrapper } from '../UIExtentions/Accordion';
import { Loading } from '../Shared/Loading';
import { ApplicationServerManagementToast } from '../ApplicationServerManagement/ApplicationServerManagementToast';
import { ServerApplicationClientEventSummary, ServerPermissionClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import _ from 'lodash';
import { ScopedUserPrincipal } from '../../permissions/ScopedUserPrincipal';
import { AppType } from '../../constants/AppType';
import { displayArray } from '../../helpers/arrayHelpers';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { VirtualAppAction } from '../../constants/VirtualAppAction';
import { VirtualAppsServerManagementButtons } from './VirtualAppsServerManagementButtons';
import { VirtualAppsServerTable } from './VirtualAppsServerTable';
import { nameof } from 'ts-simple-nameof';
import { filter } from '../Shared/Filter';
import { Col, Container, Row } from 'react-bootstrap';
import { Search } from '../Shared/Search';
import { sortByEnvironmentAndServerName } from '../../helpers/sorting';

export interface VirtualAppsActionHandlerProps {
    virtualAppName: string;
    webSiteName: string;
    servers: string[];
    action: VirtualAppAction;
}

export interface VirtualAppsServerManagementViewProps {
    userPrincipal: UserPrincipal;
    applicationName: string;
    webSiteName: string;
    isLoading: boolean;
    isDataNotFound: boolean;
    errorMessage: string;
    serverApps: IManagingVirtualAppsInstance[];
}

export const VirtualAppsServerManagementView = (props: VirtualAppsServerManagementViewProps) => {
    const [servers, setServers] = useState(new Array<string>());
    const [allSelected, setAllSelected] = useState(false);
    const [serverApps, setServerApps] = useState(props.serverApps);
    const [popupProps, setPopupProps] = useState<PopupProps | undefined>();
    const [serversUserPrincipal, setServersUserPrincipal] = useState(new ScopedUserPrincipal([]));
    const [searchTerm, setSearchTerm] = useState('');

    const applicationName = props.applicationName;
    const webSiteName = props.webSiteName;

    const filteredServerApps = useMemo(() => {
        const filterFields = [nameof<IManagingVirtualAppsInstance>((f) => f.serverName), nameof<IManagingVirtualAppsInstance>((f) => f.environment)];
        return filter(serverApps, searchTerm, filterFields);
    }, [searchTerm, serverApps]);

    const getServersUserPrincipal = async (serverApps: IManagingVirtualAppsInstance[]) => {
        const servers = _.map(serverApps, (serverApp) => serverApp.serverName);
        const permissionsResult = await AppManagerClient.getApplicationUserPermissionsForServers(AppType.VirtualApp, servers);
        const myServersUserPrincipal = new ScopedUserPrincipal(permissionsResult);
        setServersUserPrincipal(myServersUserPrincipal);
    };

    const serverSelectionChanged = useCallback(
        function (selected: boolean, serverName: string) {
            let updatedServers = [...servers];

            if (selected === true) {
                updatedServers.push(serverName);
            } else {
                updatedServers = updatedServers.filter((x) => x !== serverName);
            }

            setServers(updatedServers);
        },
        [servers]
    );

    useEffect(() => {
        getServersUserPrincipal(serverApps);
    }, [serverApps]);

    useEffect(() => {
        function isForMe(data: ServerPermissionClientEventSummary) {
            const servers = _.map(serverApps, (serverApp) => serverApp.serverName);
            return servers.includes(data.targetServer);
        }

        signalREventsClient.onServerPermissionEvent.setHook(`VirtualAppsServerManagementView_${applicationName}`, (data) => {
            if (isForMe(data)) {
                getServersUserPrincipal(serverApps);
            }
        });
    }, [applicationName, serverApps]);

    useEffect(() => {
        const local = [...props.serverApps];
        sortByEnvironmentAndServerName(local);
        setServerApps(local);
    }, [props.serverApps]);

    useEffect(() => {
        function isForMe(data: ServerApplicationClientEventSummary) {
            return data.targetApplication === applicationName;
        }

        const refreshServerApp = async function (targetApplication: string, serverName: string) {
            const serverNames = [serverName];
            const serverData = await AppManagerClient.getVirtualAppsForManagingByServers(targetApplication, serverNames);

            //update server list
            const updatedServerApps = serverApps.map((s) => {
                return serverData.find((updatedServer) => s.name === updatedServer.name && s.serverName === updatedServer.serverName) ?? s;
            });

            //add new servers
            serverData.forEach((newServer) => {
                if (updatedServerApps.find((s) => s.name === newServer.name && s.serverName === newServer.serverName) === undefined) {
                    updatedServerApps.push(newServer);
                }
            });

            //remove not found servers
            serverNames.forEach((serverName) => {
                if (serverData.find((missingServer) => targetApplication === missingServer.name && serverName === missingServer.serverName) === undefined) {
                    const removed = _.remove(updatedServerApps, (s) => s.name === targetApplication && s.serverName === serverName);
                    if (removed.length !== 0) {
                        serverSelectionChanged(false, serverName);
                    }
                }
            });

            sortByEnvironmentAndServerName(updatedServerApps);
            setServerApps(updatedServerApps);
        };

        signalREventsClient.onWebSiteEvent.setHook(`VirtualAppsServerManagementView_${applicationName}`, (data) => {
            if (isForMe(data)) {
                if (data.isSuccess) {
                    refreshServerApp(data.targetApplication, data.targetServer);
                }
                if (data.isFailure) {
                    showPopup({
                        header: `Operation on [${data.targetServer}] failed.`,
                        body: `[${data.type}]: ${data.errorMessage}`,
                        type: 'danger',
                        autohide: true,
                    });
                }
            }
        });
    }, [applicationName, serverApps, serverSelectionChanged]);

    if (serverApps) {
        const allServersSelected = serverApps.every((s: IManagingApplicationInstance) => servers.includes(s.serverName));
        if (allServersSelected !== allSelected) {
            setAllSelected(allServersSelected);
        }
    }

    const allServersSelectionChanged = function (selected: boolean) {
        const allServers = serverApps.map((s: IManagingApplicationInstance) => {
            return s.serverName;
        });
        if (selected) {
            setServers(allServers);
        } else {
            setServers([]);
        }
    };

    const VirtualAppActionHandler = async function (props: VirtualAppsActionHandlerProps) {
        if (props.action === VirtualAppAction.Refresh) {
            await tryWithErrorHandler(props.action, () => AppManagerClient.postWebSitesAction(props.webSiteName, props.action, props.servers));
            return;
        }
        await tryWithErrorHandler(props.action, () => AppManagerClient.postVirtualAppsAction(props.virtualAppName, props.action, props.servers));
    };

    const tryWithErrorHandler = async function (action: string, funct: () => Promise<IPostApplicationActionResponse>) {
        try {
            const postResult = await funct();

            showPopup({
                header: `Successfully send action [${action}] to agent.`,
                toCopy: postResult.correlationId,
                body: `Messages: ${displayArray(postResult.messages)}. Warnings: ${displayArray(postResult.warnings)}`,
            });
        } catch (error) {
            const apiError = error as ApiNonSuccessError;
            showPopup({
                header: `Error (${apiError?.status}) while calling [${action}].`,
                type: 'danger',
                body: `Error: ${apiError?.message} `,
            });
        }
    };

    const onTimeout = function (data: ServerApplicationClientEventSummary) {
        showPopup({
            header: `Operation timed out on [${data.targetServer}].`,
            body: `Operation: [${data.type}]`,
            type: 'danger',
            autohide: true,
        });
    };

    const showPopup = function (props: PopupProps) {
        setPopupProps(props);
    };

    return (
        <>
            <ApplicationServerManagementToast popupProps={popupProps} onClose={() => setPopupProps(undefined)} />

            {props.isLoading ? (
                <Loading text="Server Management" />
            ) : (
                <VirtualAppsServerManagementButtons
                    selectedServers={servers}
                    userPrincipal={props.userPrincipal}
                    serversUserPrincipal={serversUserPrincipal}
                    onClickCallback={(action: VirtualAppAction) =>
                        VirtualAppActionHandler({
                            virtualAppName: applicationName,
                            webSiteName: webSiteName,
                            servers: servers,
                            action: action,
                        })
                    }
                />
            )}

            <AccordionWrapper
                isDefaultOpen={true}
                headerIcon="fa-server"
                headerText="Servers"
                objectName={applicationName}
                isLoading={props.isLoading}
                errorMessage={props.errorMessage}
            >
                {' '}
                <Container>
                    <Row>
                        <Col>
                            <Search onUpdate={setSearchTerm} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <VirtualAppsServerTable
                                userPrincipal={props.userPrincipal}
                                serversUserPrincipal={serversUserPrincipal}
                                allServersSelectionChanged={allServersSelectionChanged}
                                serverSelectionChanged={serverSelectionChanged}
                                onClickCallback={VirtualAppActionHandler}
                                serverApps={filteredServerApps}
                                servers={servers}
                                allSelected={allSelected}
                                onTimeoutCallback={onTimeout}
                            />
                        </Col>
                    </Row>
                </Container>
            </AccordionWrapper>
        </>
    );
};
